import React, { useEffect } from 'react';
import { Button, Modal, Table, Tag, Tooltip } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

// UI meaning per device-reported OTA state. Only CONFIRMED counts as
// completed — SUCCESS_REBOOTING looks like success but the device hasn't
// actually come back up with rollback cancelled yet.
const STATE_META = {
  PENDING: { color: 'default', label: 'Pending' },
  ACCEPTED: { color: 'blue', label: 'Accepted' },
  DOWNLOADING: { color: 'processing', label: 'Downloading' },
  VERIFYING: { color: 'processing', label: 'Verifying' },
  SUCCESS_REBOOTING: { color: 'gold', label: 'Rebooting (not yet confirmed)' },
  CONFIRMED: { color: 'green', label: 'Completed' },
  REJECTED: { color: 'red', label: 'Rejected' },
  SKIPPED: { color: 'default', label: 'Already up to date' },
  FAILED: { color: 'red', label: 'Failed' },
  TIMEOUT: { color: 'red', label: 'Timed out (no response)' },
};

const OtaStatusModal = (props) => {
  const { t } = useTranslation();
  const firmware = props.firmware;
  const status = props.otaStatus;

  useEffect(() => {
    if (props.visible && firmware?.VERSION) {
      props.loadStatus(firmware.VERSION);
    }
  }, [props.visible, firmware?.VERSION]);

  const columns = [
    {
      key: 'MAC',
      title: t('FirmwarePage.otaStatusModal.mac') || 'MAC',
      dataIndex: 'MAC',
    },
    {
      key: 'STATE',
      title: t('FirmwarePage.otaStatusModal.state') || 'State',
      render: (_, row) => {
        const meta = STATE_META[row.STATE] || { color: 'default', label: row.STATE };
        const tag = <Tag color={meta.color}>{meta.label}</Tag>;
        return row.DETAIL ? <Tooltip title={row.DETAIL}>{tag}</Tooltip> : tag;
      },
    },
    {
      key: 'UPDATED_TIME',
      title: t('FirmwarePage.otaStatusModal.updatedTime') || 'Updated',
      render: (_, row) => row.UPDATED_TIME || '-',
    },
  ];

  return (
    <Modal
      title={
        (t('FirmwarePage.otaStatusModal.title') || 'OTA status') +
        (firmware?.VERSION ? ` — ${firmware.VERSION}` : '')
      }
      open={props.visible}
      onCancel={props.close}
      footer={
        <Button
          icon={<ReloadOutlined />}
          onClick={() => firmware?.VERSION && props.loadStatus(firmware.VERSION)}
        >
          {t('FirmwarePage.otaStatusModal.refresh') || 'Refresh'}
        </Button>
      }
    >
      {status?.RESULT && (
        <div style={{ marginBottom: 12 }}>
          <div>
            {t('FirmwarePage.otaStatusModal.summary', {
              confirmed: status.CONFIRMED_COUNT,
              total: status.TOTAL_COUNT,
            }) || `Confirmed on ${status.CONFIRMED_COUNT} of ${status.TOTAL_COUNT} device(s)`}
          </div>
          {status.PUSHED_TIME && (
            <div style={{ color: '#888' }}>
              {(t('FirmwarePage.otaStatusModal.pushedAt') || 'Pushed at') + ` ${status.PUSHED_TIME}`}
            </div>
          )}
        </div>
      )}
      <Table
        dataSource={status?.DEVICES || []}
        columns={columns}
        rowKey={'MAC'}
        pagination={false}
        size="small"
      />
    </Modal>
  );
};

export default OtaStatusModal;
